// Camada de acesso unificada ao Postgres (substitui localStorage do Zustand quando logado)
import { supabase } from "@/integrations/supabase/client";

export type DBAgent = {
  id: string;
  user_id: string;
  name: string;
  role: string | null;
  system_prompt: string | null;
  models: string[];
  status: string;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
};

export type DBMemory = {
  id: string;
  user_id: string;
  kind: string;
  title: string | null;
  content: string;
  tags: string[];
  metadata: Record<string, unknown>;
  created_at: string;
};

export type DBSchedule = {
  id: string;
  user_id: string;
  name: string;
  cron_expr: string;
  prompt: string;
  models: string[];
  agent_id: string | null;
  enabled: boolean;
  last_run_at: string | null;
  cron_job_id: number | null;
  created_at: string;
  updated_at: string;
};

export type DBWorkflow = {
  id: string;
  user_id: string;
  name: string;
  description: string | null;
  nodes: unknown[];
  edges: unknown[];
  created_at: string;
  updated_at: string;
};

export type DBSandboxRun = {
  id: string;
  user_id: string;
  tool_id: string;
  tool_name: string;
  input: unknown;
  output: unknown;
  hash: string;
  duration_ms: number | null;
  status: string;
  created_at: string;
};

// ============ AGENTS ============
export const agentsDB = {
  async list(): Promise<DBAgent[]> {
    const { data, error } = await supabase.from("agents").select("*").order("created_at", { ascending: false });
    if (error) throw error;
    return (data ?? []) as unknown as DBAgent[];
  },
  async create(agent: { name: string; role?: string; system_prompt?: string; models?: string[] }) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error("not authenticated");
    const { data, error } = await supabase
      .from("agents")
      .insert({
        user_id: user.id,
        name: agent.name,
        role: agent.role ?? null,
        system_prompt: agent.system_prompt ?? null,
        models: (agent.models ?? []) as never,
      })
      .select()
      .single();
    if (error) throw error;
    return data as unknown as DBAgent;
  },
  async update(id: string, patch: Partial<DBAgent>) {
    const { data, error } = await supabase.from("agents").update(patch as never).eq("id", id).select().single();
    if (error) throw error;
    return data as unknown as DBAgent;
  },
  async remove(id: string) {
    const { error } = await supabase.from("agents").delete().eq("id", id);
    if (error) throw error;
  },
};

// ============ MEMORY ============
export const memoryDB = {
  async list(): Promise<DBMemory[]> {
    const { data, error } = await supabase.from("memory_items").select("*").order("created_at", { ascending: false });
    if (error) throw error;
    return (data ?? []) as unknown as DBMemory[];
  },
  async create(item: { kind?: string; title?: string; content: string; tags?: string[] }) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error("not authenticated");
    const { data, error } = await supabase
      .from("memory_items")
      .insert({
        user_id: user.id,
        kind: item.kind ?? "short",
        title: item.title ?? null,
        content: item.content,
        tags: (item.tags ?? []) as never,
      })
      .select()
      .single();
    if (error) throw error;
    return data as unknown as DBMemory;
  },
  async remove(id: string) {
    const { error } = await supabase.from("memory_items").delete().eq("id", id);
    if (error) throw error;
  },
  async search(query: string): Promise<DBMemory[]> {
    const { data, error } = await supabase
      .from("memory_items")
      .select("*")
      .or(`content.ilike.%${query}%,title.ilike.%${query}%`)
      .limit(50);
    if (error) throw error;
    return (data ?? []) as unknown as DBMemory[];
  },
};

// ============ SCHEDULES ============
export const schedulesDB = {
  async list(): Promise<DBSchedule[]> {
    const { data, error } = await supabase.from("schedules").select("*").order("created_at", { ascending: false });
    if (error) throw error;
    return (data ?? []) as unknown as DBSchedule[];
  },
  async create(s: { name: string; cron_expr: string; prompt: string; models: string[]; agent_id?: string | null }) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error("not authenticated");
    const { data, error } = await supabase
      .from("schedules")
      .insert({
        user_id: user.id,
        name: s.name,
        cron_expr: s.cron_expr,
        prompt: s.prompt,
        models: s.models as never,
        agent_id: s.agent_id ?? null,
      })
      .select()
      .single();
    if (error) throw error;
    return data as unknown as DBSchedule;
  },
  async toggle(id: string, enabled: boolean) {
    const { error } = await supabase.from("schedules").update({ enabled }).eq("id", id);
    if (error) throw error;
  },
  async remove(id: string) {
    const { error } = await supabase.from("schedules").delete().eq("id", id);
    if (error) throw error;
  },
};

// ============ WORKFLOWS ============
export const workflowsDB = {
  async list(): Promise<DBWorkflow[]> {
    const { data, error } = await supabase.from("workflows").select("*").order("updated_at", { ascending: false });
    if (error) throw error;
    return (data ?? []) as unknown as DBWorkflow[];
  },
  async upsert(w: { id?: string; name: string; description?: string; nodes: unknown[]; edges: unknown[] }) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error("not authenticated");
    const payload = {
      user_id: user.id,
      name: w.name,
      description: w.description ?? null,
      nodes: w.nodes as never,
      edges: w.edges as never,
      ...(w.id ? { id: w.id } : {}),
    };
    const { data, error } = await supabase.from("workflows").upsert(payload).select().single();
    if (error) throw error;
    return data as unknown as DBWorkflow;
  },
  async remove(id: string) {
    const { error } = await supabase.from("workflows").delete().eq("id", id);
    if (error) throw error;
  },
};

// ============ SANDBOX (audit) ============
export const sandboxDB = {
  async log(entry: { tool_id: string; tool_name: string; input: unknown; output: unknown; hash: string; duration_ms?: number; status?: string }) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    await supabase.from("sandbox_runs").insert({
      user_id: user.id,
      tool_id: entry.tool_id,
      tool_name: entry.tool_name,
      input: entry.input as never,
      output: entry.output as never,
      hash: entry.hash,
      duration_ms: entry.duration_ms ?? null,
      status: entry.status ?? "success",
    });
  },
  async list(limit = 100): Promise<DBSandboxRun[]> {
    const { data, error } = await supabase
      .from("sandbox_runs")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(limit);
    if (error) throw error;
    return (data ?? []) as unknown as DBSandboxRun[];
  },
};

// ============ MARKETPLACE ============
export const marketplaceDB = {
  async list() {
    const { data, error } = await supabase
      .from("marketplace_items")
      .select("*")
      .eq("is_public", true)
      .order("installs", { ascending: false });
    if (error) throw error;
    return data ?? [];
  },
  async publish(item: { kind: "agent" | "workflow" | "tool"; name: string; description?: string; payload: unknown }) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error("not authenticated");
    const { data, error } = await supabase
      .from("marketplace_items")
      .insert({
        author_id: user.id,
        kind: item.kind,
        name: item.name,
        description: item.description ?? null,
        payload: item.payload as never,
      })
      .select()
      .single();
    if (error) throw error;
    return data;
  },
  async install(itemId: string) {
    // increment installs
    const { data: item } = await supabase.from("marketplace_items").select("*").eq("id", itemId).single();
    if (!item) throw new Error("item not found");
    await supabase.from("marketplace_items").update({ installs: (item.installs ?? 0) + 1 }).eq("id", itemId);
    // clone payload into user's space
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error("not authenticated");
    if (item.kind === "agent") {
      const a = item.payload as { name: string; role?: string; system_prompt?: string; models?: string[] };
      return await agentsDB.create({ name: `${a.name} (installed)`, role: a.role, system_prompt: a.system_prompt, models: a.models });
    }
    if (item.kind === "workflow") {
      const w = item.payload as { name: string; description?: string; nodes: unknown[]; edges: unknown[] };
      return await workflowsDB.upsert({ name: `${w.name} (installed)`, description: w.description, nodes: w.nodes, edges: w.edges });
    }
    return item;
  },
};

// ============ GOVERNANCE POLICIES ============
export type DBPolicy = {
  id: string;
  user_id: string;
  name: string;
  description: string | null;
  policy_type: string;
  config: Record<string, unknown>;
  enabled: boolean;
  created_at: string;
  updated_at: string;
};

export const policiesDB = {
  async list(): Promise<DBPolicy[]> {
    const { data, error } = await supabase
      .from("governance_policies")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) throw error;
    return (data ?? []) as unknown as DBPolicy[];
  },
  async create(p: { name: string; description?: string; policy_type?: string; config?: Record<string, unknown>; enabled?: boolean }) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error("not authenticated");
    const { data, error } = await supabase
      .from("governance_policies")
      .insert({
        user_id: user.id,
        name: p.name,
        description: p.description ?? null,
        policy_type: p.policy_type ?? "cost_limit",
        config: (p.config ?? {}) as never,
        enabled: p.enabled ?? true,
      })
      .select()
      .single();
    if (error) throw error;
    return data as unknown as DBPolicy;
  },
  async toggle(id: string, enabled: boolean) {
    const { error } = await supabase.from("governance_policies").update({ enabled }).eq("id", id);
    if (error) throw error;
  },
  async remove(id: string) {
    const { error } = await supabase.from("governance_policies").delete().eq("id", id);
    if (error) throw error;
  },
};

// ============ ANALYTICS / DEVOPS ============
export const analyticsDB = {
  async last24h() {
    const since = new Date(Date.now() - 24 * 3600 * 1000).toISOString();
    const [sandboxRes, workflowRes, chatRes] = await Promise.all([
      supabase.from("sandbox_runs").select("tool_name, duration_ms, status").gte("created_at", since),
      supabase.from("workflow_runs").select("status, started_at, finished_at").gte("started_at", since),
      supabase.from("chat_messages").select("id, created_at").gte("created_at", since),
    ]);
    const sandbox = sandboxRes.data ?? [];
    const workflows = workflowRes.data ?? [];
    const chats = chatRes.data ?? [];
    const ms = sandbox.filter((r) => r.duration_ms).map((r) => r.duration_ms!);
    const wfMs = workflows
      .filter((w) => w.finished_at)
      .map((w) => new Date(w.finished_at!).getTime() - new Date(w.started_at).getTime());
    return {
      sandbox_runs: sandbox.length,
      sandbox_errors: sandbox.filter((r) => r.status !== "success").length,
      sandbox_avg_ms: ms.length ? Math.round(ms.reduce((a, b) => a + b, 0) / ms.length) : 0,
      workflow_runs: workflows.length,
      workflow_errors: workflows.filter((w) => w.status === "failed").length,
      workflow_avg_ms: wfMs.length ? Math.round(wfMs.reduce((a, b) => a + b, 0) / wfMs.length) : 0,
      chat_messages: chats.length,
      by_tool: Object.entries(
        sandbox.reduce<Record<string, number>>((acc, r) => {
          acc[r.tool_name] = (acc[r.tool_name] ?? 0) + 1;
          return acc;
        }, {}),
      ).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count).slice(0, 10),
    };
  },
};

// ============ AUDIT (combined feed) ============
export type AuditEntry = {
  id: string;
  source: "sandbox" | "workflow" | "chat";
  label: string;
  status: string;
  created_at: string;
  duration_ms: number | null;
  hash: string | null;
};

export const auditDB = {
  async feed(limit = 200): Promise<AuditEntry[]> {
    const [sandboxRes, workflowRes, chatRes] = await Promise.all([
      supabase.from("sandbox_runs").select("id, tool_name, status, created_at, duration_ms, hash").order("created_at", { ascending: false }).limit(limit),
      supabase.from("workflow_runs").select("id, status, started_at, finished_at, workflow_id").order("started_at", { ascending: false }).limit(limit),
      supabase.from("chat_messages").select("id, role, created_at, models").order("created_at", { ascending: false }).limit(limit),
    ]);
    const entries: AuditEntry[] = [];
    for (const r of sandboxRes.data ?? []) {
      entries.push({
        id: `s_${r.id}`, source: "sandbox", label: `tool:${r.tool_name}`,
        status: r.status, created_at: r.created_at, duration_ms: r.duration_ms ?? null, hash: r.hash ?? null,
      });
    }
    for (const w of workflowRes.data ?? []) {
      const dur = w.finished_at ? new Date(w.finished_at).getTime() - new Date(w.started_at).getTime() : null;
      entries.push({
        id: `w_${w.id}`, source: "workflow", label: `workflow:${(w.workflow_id ?? "").slice(0, 8)}`,
        status: w.status, created_at: w.started_at, duration_ms: dur, hash: null,
      });
    }
    for (const c of chatRes.data ?? []) {
      entries.push({
        id: `c_${c.id}`, source: "chat", label: `chat:${c.role}`,
        status: "success", created_at: c.created_at, duration_ms: null, hash: null,
      });
    }
    return entries.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()).slice(0, limit);
  },
};

// hash helper for sandbox audit
export async function sha512(input: string): Promise<string> {
  const buf = new TextEncoder().encode(input);
  const hash = await crypto.subtle.digest("SHA-512", buf);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("")
    .slice(0, 32);
}
